import turfArea from '@turf/area';
import { SEVERITY_LABELS } from '../config/regulations.js';
import { STATUS_LABELS } from '../config/future-changes.js';
import { isLLMAvailable, generateSummary } from '../services/llm.js';
import { getOverlapSummary } from '../analysis/rules-engine.js';
import { OWN_PARCEL_IDS } from '../config/user.js';

const sidebarContent = () => document.getElementById('sidebar-content');

export function showPlaceholder() {
  sidebarContent().innerHTML = `
    <div class="sidebar-placeholder">
      <div class="placeholder-icon">🗺️</div>
      <p>Klik op een perceel om de geldende regelgeving te bekijken</p>
    </div>
  `;
}

export function showParcelInfo(feature, regulations, overlaps, futureChanges) {
  const props = feature.properties;
  const area = turfArea(feature);
  const areaHa = (area / 10000).toFixed(2);
  const cropName = props.gewas || props.gewasnaam || props.GWS_GEWAS || 'Onbekend';
  const parcelId = props.id || props.objectid || feature.id || 'Onbekend';
  const isOwn = OWN_PARCEL_IDS.includes(parcelId);
  const overlapNames = getOverlapSummary(overlaps);

  // Calculate buffer overlap for available area
  const bufferOverlap = overlaps
    .filter((o) => o.layerId === 'buffer-zones')
    .reduce((sum, o) => sum + (o.overlapArea || 0), 0);
  const bufferOverlapHa = (bufferOverlap / 10000).toFixed(2);
  const availableArea = Math.max(0, area - bufferOverlap);
  const availableHa = (availableArea / 10000).toFixed(2);

  let regulationsHTML;
  if (regulations.length === 0) {
    regulationsHTML = `
      <div class="no-regulations">
        <div class="check-icon">✓</div>
        <p>Geen bijzondere regelgeving van toepassing</p>
      </div>
    `;
  } else {
    regulationsHTML = `
      <div class="regulations-section">
        <h3>Geldende regelgeving (${regulations.length})</h3>
        ${regulations.map((r) => renderRegulationCard(r)).join('')}
      </div>
    `;
  }

  let aiHTML = '';
  if (isLLMAvailable() && regulations.length > 0) {
    aiHTML = `
      <div class="ai-summary-section">
        <button class="ai-summary-btn" id="ai-summary-btn">
          Genereer samenvatting (AI)
        </button>
        <div id="ai-summary-result"></div>
      </div>
    `;
  }

  const futureHTML = renderFutureChangesPanel(futureChanges);

  sidebarContent().innerHTML = `
    <div class="disclaimer-banner">
      Dit is indicatieve informatie. Aan deze gegevens kunnen geen rechten worden ontleend.
    </div>
    <div class="parcel-header">
      <h2>${cropName}${isOwn ? ' <span class="own-parcel-badge">Mijn perceel</span>' : ''}</h2>
      <span class="parcel-id-subtle">${parcelId}</span>
      <div class="area-summary">
        <div class="area-row">
          <span class="area-label">Totale oppervlakte</span>
          <span class="area-value">${areaHa} ha</span>
        </div>
        ${bufferOverlap > 0 ? `
        <div class="area-row overlap">
          <span class="area-label">Teeltvrije zone</span>
          <span class="area-value">-${bufferOverlapHa} ha</span>
        </div>
        ` : ''}
        <div class="area-row available">
          <span class="area-label">Beschikbare oppervlakte</span>
          <span class="area-value">${availableHa} ha</span>
        </div>
      </div>
      ${overlapNames.length > 0 ? `
        <div class="overlap-tags">${overlapNames.join(', ')}</div>
      ` : ''}
    </div>
    <div class="sidebar-tabs">
      <button class="sidebar-tab active" data-tab="current">Regelgeving</button>
      <button class="sidebar-tab" data-tab="future">Wijzigingen</button>
      <button class="sidebar-tab" data-tab="activities">Activiteiten</button>
    </div>
    <div class="tab-panel active" id="tab-current">
      ${renderMestSummary(availableHa)}
      <button class="grondmonsters-btn" id="grondmonsters-btn">Voeg grondmonsters toe</button>
      ${regulationsHTML}
      ${aiHTML}
    </div>
    <div class="tab-panel" id="tab-future">
      ${futureHTML}
    </div>
    <div class="tab-panel" id="tab-activities">
      ${renderActivitiesPanel()}
    </div>
  `;

  setupTabSwitching();
  setupFeedbackLinks();

  // Wire up AI button
  const aiBtn = document.getElementById('ai-summary-btn');
  if (aiBtn) {
    aiBtn.addEventListener('click', () => {
      handleAISummary(aiBtn, {
        crop: cropName,
        area: `${areaHa} ha`,
        overlaps: overlapNames,
      }, regulations);
    });
  }

  // Wire up grondmonsters button
  const grondBtn = document.getElementById('grondmonsters-btn');
  if (grondBtn) {
    grondBtn.addEventListener('click', () => {
      showComingSoonModal('Grondmonsters toevoegen');
    });
  }
}

function renderActivitiesPanel() {
  const activities = [
    {
      title: 'Inspectie waterschap Vechtstromen',
      date: '12 maart 2026',
      result: 'Geen bijzonderheden',
    },
    {
      title: 'Dronecontrole NVWA',
      date: '8 februari 2026',
      result: 'Perceelgrenzen conform opgave',
    },
    {
      title: 'Bodemkwaliteitscheck',
      date: '15 januari 2026',
      result: 'Voldoet aan normen',
    },
  ];

  return `
    <p class="tab-intro">
      Overzicht van recente activiteiten en inspecties op dit perceel.
    </p>
    <div class="activities-list">
      ${activities.map((a) => `
        <div class="activity-card positive">
          <span class="activity-icon">✓</span>
          <div class="activity-content">
            <h4>${a.title}</h4>
            <span class="activity-date">${a.date}</span>
            <span class="activity-result">${a.result}</span>
          </div>
        </div>
      `).join('')}
    </div>
  `;
}

function renderMestSummary(availableHa) {
  const maxStikstof = (parseFloat(availableHa) * 170).toFixed(0);
  return `
    <div class="mest-summary">
      <h3>Indicatieve mestberekening</h3>
      <div class="mest-row">
        <span>Beschikbare oppervlakte</span>
        <span>${availableHa} ha</span>
      </div>
      <div class="mest-row highlight">
        <span>Max. stikstof (indicatief)</span>
        <span>${maxStikstof} kg N</span>
      </div>
      <p class="mest-note">Berekening op basis van 170 kg N/ha (standaardnorm). Raadpleeg RVO voor de exacte normen voor uw situatie.</p>
    </div>
  `;
}

function renderFutureChangesPanel(futureChanges) {
  const relevant = futureChanges.filter((c) => c.isRelevant);
  const other = futureChanges.filter((c) => !c.isRelevant);

  let relevantHTML = '';
  if (relevant.length > 0) {
    relevantHTML = `
      <div class="future-changes-section">
        <h3>Relevant voor dit perceel (${relevant.length})</h3>
        ${relevant.map((c) => renderChangeCard(c)).join('')}
      </div>
    `;
  }

  let otherHTML = '';
  if (other.length > 0) {
    otherHTML = `
      <div class="future-changes-section other-changes">
        <h3>Overige verwachte wijzigingen (${other.length})</h3>
        ${other.map((c) => renderChangeCard(c)).join('')}
      </div>
    `;
  }

  return `
    <p class="tab-intro">
      Onderstaande beleidswijzigingen zijn in voorbereiding en kunnen gevolgen hebben voor uw bedrijfsvoering.
      Deze informatie is indicatief en kan wijzigen.
    </p>
    ${relevantHTML}
    ${otherHTML}
  `;
}

function renderChangeCard(change) {
  const statusLabel = STATUS_LABELS[change.status] || change.status;
  const relevanceTag = change.isRelevant
    ? `<div class="relevance-tag">Relevant: ${change.relevanceReason}</div>`
    : '';
  const linkHTML = change.link
    ? `<a href="${change.link}" target="_blank" rel="noopener">Meer informatie →</a>`
    : '';

  const expandId = `expand-${change.id}`;
  return `
    <div class="change-card impact-${change.impact} ${change.isRelevant ? 'is-relevant' : ''}">
      <div class="card-header">
        <h4>${change.title}</h4>
        <span class="status-badge ${change.status}">${statusLabel}</span>
      </div>
      <input type="checkbox" class="card-expand-toggle" id="${expandId}">
      <p>${change.summary}</p>
      ${change.reason ? `<div class="change-reason"><strong>Waarom?</strong> ${change.reason}</div>` : ''}
      <label class="card-expand" for="${expandId}">Lees meer</label>
      <div class="card-footer">
        <span class="change-date">Verwacht ${change.expectedDate}</span>
        <span class="change-source">Bron: ${change.source}</span>
      </div>
      <div class="card-actions">
        ${linkHTML}
        <a href="#" class="feedback-link" data-feedback="${change.title}">Feedback geven →</a>
      </div>
      ${relevanceTag}
    </div>
  `;
}

function renderRegulationCard(regulation) {
  const severityLabel = SEVERITY_LABELS[regulation.severity] || regulation.severity;
  const overlapInfo = regulation.overlapArea != null
    ? `<span class="overlap-info">${(regulation.overlapArea / 10000).toFixed(2)} ha overlap</span>`
    : '';
  const linkHTML = regulation.link
    ? `<a href="${regulation.link}" target="_blank" rel="noopener">Meer informatie →</a>`
    : '';

  const expandId = `expand-${regulation.id}`;
  return `
    <div class="regulation-card severity-${regulation.severity}">
      <div class="card-header">
        <h4>${regulation.title}</h4>
        <span class="severity-badge ${regulation.severity}">${severityLabel}</span>
      </div>
      <input type="checkbox" class="card-expand-toggle" id="${expandId}">
      <p>${regulation.summary}</p>
      <label class="card-expand" for="${expandId}">Lees meer</label>
      ${overlapInfo ? `<div style="margin-bottom:4px">${overlapInfo}</div>` : ''}
      <div class="card-links">
        ${regulation.source ? `<span class="regulation-source">Bron: <a href="${regulation.link}" target="_blank" rel="noopener">${regulation.source} ↗</a></span>` : ''}
        ${linkHTML}
        <a href="#" class="feedback-link" data-feedback="${regulation.title}">Feedback geven →</a>
      </div>
    </div>
  `;
}

function setupTabSwitching() {
  const tabBar = sidebarContent().querySelector('.sidebar-tabs');
  if (!tabBar) return;

  tabBar.addEventListener('click', (e) => {
    const btn = e.target.closest('.sidebar-tab');
    if (!btn) return;

    const tabId = btn.dataset.tab;

    tabBar.querySelectorAll('.sidebar-tab').forEach((t) => t.classList.remove('active'));
    btn.classList.add('active');

    sidebarContent().querySelectorAll('.tab-panel').forEach((p) => p.classList.remove('active'));
    const panel = document.getElementById(`tab-${tabId}`);
    if (panel) panel.classList.add('active');

    document.getElementById('sidebar').scrollTop = 0;
  });
}

function setupFeedbackLinks() {
  sidebarContent().addEventListener('click', (e) => {
    const link = e.target.closest('.feedback-link');
    if (!link) return;
    e.preventDefault();
    showFeedbackModal(link.dataset.feedback);
  });
}

function showFeedbackModal(ruleTitle) {
  // Remove any existing modal
  const existing = document.querySelector('.feedback-modal-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.className = 'feedback-modal-overlay';
  overlay.innerHTML = `
    <div class="feedback-modal">
      <h3>Feedback</h3>
      <p class="feedback-rule-context">${ruleTitle}</p>
      <p>De feedbackfunctionaliteit wordt binnenkort beschikbaar. Hiermee kunt u uw ervaring met deze regelgeving delen.</p>
      <button class="feedback-modal-close">Sluiten</button>
    </div>
  `;

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || e.target.closest('.feedback-modal-close')) {
      overlay.remove();
    }
  });

  document.body.appendChild(overlay);
}

function showComingSoonModal(title) {
  const existing = document.querySelector('.feedback-modal-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.className = 'feedback-modal-overlay';
  overlay.innerHTML = `
    <div class="feedback-modal">
      <h3>${title}</h3>
      <p>Deze functionaliteit wordt binnenkort beschikbaar. U kunt dan grondmonsterresultaten koppelen aan uw percelen voor een nauwkeurigere analyse.</p>
      <button class="feedback-modal-close">Sluiten</button>
    </div>
  `;

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || e.target.closest('.feedback-modal-close')) {
      overlay.remove();
    }
  });

  document.body.appendChild(overlay);
}

async function handleAISummary(btn, parcelInfo, regulations) {
  btn.disabled = true;
  btn.textContent = 'Samenvatting genereren...';
  const resultEl = document.getElementById('ai-summary-result');

  try {
    const summary = await generateSummary(parcelInfo, regulations);
    resultEl.innerHTML = `
      <div class="ai-summary-result">
        <span class="ai-badge">AI</span>
        <p>${summary}</p>
      </div>
    `;
    btn.style.display = 'none';
  } catch (err) {
    resultEl.innerHTML = `
      <div class="ai-summary-result" style="background:#fde8e8">
        <p style="color:var(--color-severity-high)">Fout bij genereren samenvatting: ${err.message}</p>
      </div>
    `;
    btn.disabled = false;
    btn.textContent = 'Opnieuw proberen';
  }
}
